/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  utcDate,
  APP_MIN_DATE,
  APP_MAX_DATE,
} from 'uplisting-frontend/utils/date';

interface IArgs {
  value: Date | undefined;
  allowFuture?: boolean;
  onDateSelect(newDate: Date): void;
}

interface PickerMonthSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export interface IItemConfig {
  year: number;
  date: Date;
  allowFuture: boolean;
  onClick(month: number): void;
}

export default class UiPickerMonthComponent extends Component<PickerMonthSignature> {
  @tracked selectedDate = this.args.value ?? new Date();
  @tracked activeYear = this.selectedDate.getFullYear();

  @cached
  get allowFuture(): boolean {
    return this.args.allowFuture ?? false;
  }

  @cached
  get itemConfig(): IItemConfig {
    return {
      year: this.activeYear,
      date: this.selectedDate,
      allowFuture: this.allowFuture,
      onClick: this.handleMonthClick.bind(this),
    };
  }

  @cached
  get canDecreaseYear(): boolean {
    const newYear = this.activeYear - 1;
    const minYear = APP_MIN_DATE.getFullYear();

    return newYear >= minYear;
  }

  @cached
  get canIncreaseYear(): boolean {
    if (!this.allowFuture) {
      return this.activeYear < new Date().getFullYear();
    }

    const newYear = this.activeYear + 1;
    const maxYear = APP_MAX_DATE.getFullYear();

    return newYear <= maxYear;
  }

  @action
  handleDecreaseYear(e: MouseEvent): void {
    e.preventDefault();

    if (!this.canDecreaseYear) {
      return;
    }

    this.activeYear--;
  }

  @action
  handleIncreaseYear(e: MouseEvent): void {
    e.preventDefault();

    if (!this.canIncreaseYear) {
      return;
    }

    this.activeYear++;
  }

  @action
  handleMonthClick(month: number): void {
    this.selectedDate = utcDate(new Date(this.activeYear, month));

    this.args.onDateSelect(this.selectedDate);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Picker::Month': typeof UiPickerMonthComponent;
  }
}
